import { type ElementType, isValidElement, memo, type ReactElement, useCallback, useId } from 'react';

import { Check, Minus } from '@air/next-icons';
import { Checkbox } from '@air/primitive-checkbox';

import { FILTER_ROW_CHECKBOX } from '~/constants/testIDs';

type TypeFilterRowProps = {
  icon?: ElementType | ReactElement;
  label: string;
  isPartialSelected?: boolean;
  isSelected: boolean;
  onChange: (isSelected: boolean) => void;
};

export const FilterRowWithCheckbox = memo(
  ({ icon: Icon, onChange, label, isPartialSelected, isSelected }: TypeFilterRowProps) => {
    const id = useId();

    const renderIcon = useCallback(() => {
      if (!Icon) return null;
      if (isValidElement(Icon)) return Icon;
      if (typeof Icon === 'function') {
        const IconComponent = Icon;
        return <IconComponent className="size-4 shrink-0 text-grey-10" />;
      }
      return null;
    }, [Icon]);

    const renderIndicator = useCallback(() => {
      if (isPartialSelected) {
        return <Minus />;
      }

      return <Check />;
    }, [isPartialSelected]);

    return (
      <label className="mb-0.5 flex cursor-pointer justify-between py-1.5 pr-1 pl-1.5" htmlFor={id}>
        <div className="flex items-center gap-2">
          {renderIcon()}
          <span className="text-14 text-grey-12">{label}</span>
        </div>

        <Checkbox
          id={id}
          renderIndicator={renderIndicator}
          aria-label={label}
          checked={isSelected}
          data-testid={`${FILTER_ROW_CHECKBOX}-${label}`}
          onCheckedChange={onChange}
        />
      </label>
    );
  },
);

FilterRowWithCheckbox.displayName = 'FilterRowWithCheckbox';
